import { useEffect } from "react";
import clsx from "clsx";
import { Button } from "@/components/ui/Button";
import { auftragMiniFromProject } from "@/lib/orderDraftUtils";
import type { OrderedMaterialLine } from "@/lib/projectOverview";
import type { Order } from "@/lib/projects";
import { useOrderWizard } from "@/pages/bestellungen/useOrderWizard";
import { orderCardFacts } from "@/projectOverview/orderCardFacts";
import { orderLineDisplay } from "@/projectOverview/orderLineDisplay";
import { OverviewCard } from "@/projectOverview/OverviewCard";
import { WerkszeugnisBadge } from "@/pages/bestellungen/OrdersHistorySection";

export function OrdersSection({
  orders,
  project,
  canCreate = false,
  onChanged,
}: {
  orders: OrderedMaterialLine[];
  project: Order;
  canCreate?: boolean;
  onChanged?: () => void;
}) {
  const { openNew, openDetailByBestellnummer, subscribeOrdersChanged } = useOrderWizard();

  useEffect(() => {
    if (!onChanged) return;
    return subscribeOrdersChanged(onChanged);
  }, [onChanged, subscribeOrdersChanged]);
  const grouped = new Map<string, OrderedMaterialLine[]>();
  for (const line of orders) {
    const key = String(line.bestellnummer || "Ohne Bestellnummer");
    if (!grouped.has(key)) grouped.set(key, []);
    grouped.get(key)!.push(line);
  }

  return (
    <OverviewCard title="Bestellungen & Artikel">
      {canCreate ? (
        <div className="mb-3 flex flex-wrap gap-2">
          <Button
            type="button"
            size="sm"
            onClick={() => openNew({ auftrag: auftragMiniFromProject(project) })}
          >
            Neu
          </Button>
        </div>
      ) : null}
      {!orders.length ? (
        <p className="text-sm text-label-tertiary">Keine bestellten Materialien.</p>
      ) : (
        <div className="eh-po-orders-grid">
          {[...grouped.entries()].map(([bn, lines]) => {
            const cancelled = lines.some((line) => Boolean(line.order_cancelled));
            const hasWerkszeugnis = lines.some((line) => Boolean(line.has_werkszeugnis));
            const facts = orderCardFacts(lines);
            const canOpen = bn !== "Ohne Bestellnummer";
            return (
            <article
              key={bn}
              className={clsx(
                "eh-po-order-card",
                cancelled && "eh-po-order-card--cancelled",
                !cancelled && facts.delivery === "delivered" && "eh-po-order-card--delivered",
                !cancelled &&
                  (facts.delivery === "open" || facts.delivery === "partial") &&
                  "eh-po-order-card--open",
              )}
            >
              <h3 className="eh-po-order-card-title">
                {canOpen ? (
                  <button
                    type="button"
                    className="eh-po-order-open"
                    aria-label={`Bestellung ${bn} öffnen`}
                    onClick={() => void openDetailByBestellnummer(bn)}
                  >
                    {bn}
                  </button>
                ) : (
                  <span>{bn}</span>
                )}
                {facts.statusLabel ? (
                  <span
                    className={clsx(
                      "eh-po-order-status-badge",
                      facts.delivery === "delivered" && "eh-po-order-status-badge--delivered",
                      (facts.delivery === "open" || facts.delivery === "partial") &&
                        "eh-po-order-status-badge--open",
                    )}
                  >
                    {facts.statusLabel}
                  </span>
                ) : null}
                {hasWerkszeugnis ? <WerkszeugnisBadge /> : null}
                {cancelled ? (
                  <span
                    className="eh-po-order-cancelled-badge rounded-full border border-status-red/30 bg-status-red/15 px-2 py-0.5 text-xs font-semibold text-status-red"
                    aria-label="Storniert"
                  >
                    Storniert
                  </span>
                ) : null}
              </h3>
              {facts.meta ? <p className="eh-po-order-meta">{facts.meta}</p> : null}
              <ul className="eh-po-order-items">
                {lines.map((line, idx) => {
                  const { qty, unit, title } = orderLineDisplay(line);
                  return (
                    <li key={`${bn}-${line.id ?? idx}`} className="eh-po-order-item">
                      <span className="eh-po-order-item-main">
                        {qty ? (
                          <span className="eh-po-order-qty tabular-nums">{qty}</span>
                        ) : null}
                        {unit ? <span className="eh-po-order-unit">{unit}</span> : null}
                        <span className="eh-po-order-label">{title}</span>
                        {(line.lot_nummer || "").trim() ? (
                          <span className="eh-po-order-unit">LOT {line.lot_nummer}</span>
                        ) : null}
                      </span>
                    </li>
                  );
                })}
              </ul>
            </article>
          );
          })}
        </div>
      )}
    </OverviewCard>
  );
}
